import React, { useEffect, useRef, useContext, useState } from "react";
import { createChart } from "lightweight-charts";
import BrokerService from "../../services/broker.service";
import AppContext from "../../context/Context";
import logo from "../../assets/img/illustrations/falcon.png";

const PnlChart = ({
  brokerId, 
  brokerName, 
  userId,
  strategyId,
  baskets = [], 
  positions = [],
  showSummary = true,
  height = 400,
  level: externalLevel,
  selectedBasket: externalSelectedBasket,
  selectedPosition: externalSelectedPosition
}) => {

  const { config: { isDark } } = useContext(AppContext);
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const lineSeriesRef = useRef(null);
  const ddSeriesRef = useRef(null);
  const [equityData, setEquityData] = useState([]);
  const [drawdownData, setDrawdownData] = useState([]);
  const [level, setLevel] = useState(externalLevel || "broker");
  const [selectedBasket, setSelectedBasket] = useState(externalSelectedBasket || baskets?.[0]?.id);
  const [selectedPosition, setSelectedPosition] = useState(externalSelectedPosition || positions?.[0]?.id);
  const [summary, setSummary] = useState({
    totalPnl: 0,
    maxDd: 0,
    maxDdPct: 0,
    start: null,
    end: null,
    minPnl: 0,
    minPnlTs: null,
    maxPnl: 0,
    maxPnlTs: null,
    maxDdTs: null,
  });

  useEffect(() => {
    if (externalLevel !== undefined) setLevel(externalLevel);
  }, [externalLevel]);

  useEffect(() => {
    if (externalSelectedBasket !== undefined) setSelectedBasket(externalSelectedBasket);
  }, [externalSelectedBasket]);

  useEffect(() => {
    if (externalSelectedPosition !== undefined) setSelectedPosition(externalSelectedPosition);
  }, [externalSelectedPosition]);

  const displayBrokerName = brokerName
    ? `${String(brokerName).charAt(0).toUpperCase()}${String(brokerName).slice(1)}`
    : null;

  const LEVELS = [
    { key: "position", label: "Position" },
    { key: "basket", label: "Strategy" },
    ...(externalLevel === 'algo-broker' || level === 'algo-broker'
      ? [{ key: "algo-broker", label: displayBrokerName ? `Account (${displayBrokerName})` : "Account" }]
      : [{ key: "broker", label: displayBrokerName ? `Account (${displayBrokerName})` : "Account" }]
    ),
    { key: "user", label: "User" },
  ];

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: height,
      layout: { background: { color: 'transparent' }, textColor: isDark ? "#e5e7eb" : "#333" },
      rightPriceScale: { visible: true },
      timeScale: { timeVisible: true, secondsVisible: false, tickMarkFormatter: (time) => {
        const d = new Date(time * 1000);
        return d.toLocaleTimeString("en-IN", {
          timeZone: "Asia/Kolkata",
          hour: "2-digit",
          minute: "2-digit",
        });
      }, },
      grid: { 
        vertLines: { color: isDark ? "#1f2937" : "#eee" }, 
        horzLines: { color: isDark ? "#1f2937" : "#eee" } 
      },
      localization: {
        timeFormatter: (time) => {
          const d = new Date(time * 1000);
          return d.toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            hour: "2-digit",
            minute: "2-digit",
            day: "2-digit",
            month: "2-digit",
          });
        },
      },
    });

    const ddSeries = chart.addBaselineSeries({
      priceScaleId: "right",
      baseValue: { type: 'price', price: 0 },
      topLineColor: 'transparent',
      topFillColor1: 'transparent',
      topFillColor2: 'transparent',
      bottomLineColor: 'transparent',
      bottomFillColor1: isDark ? "rgba(248,113,113,0.3)" : "rgba(229,57,53,0.3)",
      bottomFillColor2: isDark ? "rgba(248,113,113,0.2)" : "rgba(229,57,53,0.2)",
      lineWidth: 0,
      priceFormat: { type: "price", precision: 2, minMove: 0.01 },
    });

    const series = chart.addLineSeries({ 
      color: isDark ? "#60a5fa" : "#1e88e5",
      lineWidth: 2, 
      priceScaleId: "right",
    });

    chart.subscribeCrosshairMove((param) => {
      if (!param.time) return;
  
      const d = new Date(param.time * 1000);
      const ist = d.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    });

    chartRef.current = chart;
    lineSeriesRef.current = series;
    ddSeriesRef.current = ddSeries;
    if (equityData && equityData.length) {
      lineSeriesRef.current.setData(equityData);
    }
    if (drawdownData && drawdownData.length) {
      ddSeriesRef.current.setData(drawdownData);
    }
    
    const handle = () => {
      chart.applyOptions({ width: containerRef.current.clientWidth });
    };
    window.addEventListener("resize", handle);
    
    return () => {
      window.removeEventListener("resize", handle);
      chart.remove();
      chartRef.current = null;
      lineSeriesRef.current = null;
      ddSeriesRef.current = null;
    };
  }, [isDark, height]);

  useEffect(() => {
    async function load() {
      if (!lineSeriesRef.current || !chartRef.current) return;
      let res;
      if (level === "broker" && brokerId) {
        res = await BrokerService.getBrokerPnlMtm(brokerId);
      } else if (level === "basket" && selectedBasket) {
        res = await BrokerService.getBasketPnlMtm(selectedBasket);
      } else if (level === "strategy" && strategyId) {
        res = await BrokerService.getStrategyPnlMtm(strategyId);
      } else if (level === "position" && selectedPosition) {
        res = await BrokerService.getPositionPnlMtm(selectedPosition);
      } else if (level === "user" && userId) {
        res = await BrokerService.getUserPnlMtm(userId);
      } else if (level === "algo-broker" && brokerId) {
        res = await BrokerService.getBrokerAlgoMtm(brokerId);
      } else {
        return;
      }
  
      const raw = (res?.data || [])
        .map((d) => ({
          time: d.timestamp, 
          value: Number(d.unrealised_pnl || 0) + Number(d.realised_pnl || 0),
        }))
        .sort((a, b) => a.time - b.time); 
  
      const clipped = raw.filter((pt) => {
        const istDate = new Date((pt.time + 19800) * 1000);
        const h = istDate.getUTCHours();
        const m = istDate.getUTCMinutes();
        return h < 15 || (h === 15 && m <= 31);
      });

      if (!clipped.length) return;
  
      const equity = [];
      let cumulative = 0;
      for (let i = 0; i < clipped.length; i++) {
        cumulative = clipped[i].value;
        equity.push({ time: clipped[i].time, value: Number(cumulative) });
      }
  
      const calculateDrawdown = (equity) => {
        if (!equity.length) return { drawdown: [], maxDrawdown: 0, maxDrawdownTs: null, maxDdPct: 0 };
  
        let firstPositiveIndex = -1;
        for (let i = 0; i < equity.length; i++) {
          if (equity[i].value > 0) {
            firstPositiveIndex = i;
            break;
          }
        }
  
        if (firstPositiveIndex === -1) {

          const drawdown = equity.map(pt => ({ time: pt.time, value: pt.value }));
          let minVal = equity[0].value;
          let minTs = equity[0].time;
          for (let i = 1; i < equity.length; i++) {
            if (equity[i].value < minVal) {
              minVal = equity[i].value;
              minTs = equity[i].time;
            }
          }
          return { 
            drawdown,
            maxDrawdown: minVal,
            maxDrawdownTs: minTs,
            maxDdPct: 0,
            maxDrawdownPeakValue: 0
          };
        }
  
        let runningPeak = equity[firstPositiveIndex].value;
        let maxDrawdown = 0;
        let maxDrawdownTs = null;
        let maxDrawdownPeakValue = 0;

        const drawdown = [];
        for (let index = 0; index < equity.length; index++) {
          const pt = equity[index];
          let dd;
          if (index < firstPositiveIndex) {
            dd = pt.value;
            if (dd < maxDrawdown) {
              maxDrawdown = dd;
              maxDrawdownTs = pt.time;
              maxDrawdownPeakValue = 0;
            }
          } else {
            if (pt.value > runningPeak) {
              runningPeak = pt.value;
            }
            dd = pt.value - runningPeak;
            if (dd < maxDrawdown) {
              maxDrawdown = dd;
              maxDrawdownTs = pt.time;
              maxDrawdownPeakValue = runningPeak;
            }
          }
          drawdown.push({ time: pt.time, value: dd });
        }
  
        const maxDdPct = maxDrawdownPeakValue !== 0 ? (maxDrawdown / maxDrawdownPeakValue) * 100 : 0;
  
        return { 
          drawdown, 
          maxDrawdown, 
          maxDrawdownTs, 
          maxDdPct,
          maxDrawdownPeakValue
        };
      };
  
      const drawdownResult = calculateDrawdown(equity);
      const drawdown = drawdownResult.drawdown;
  
      let minPnl = equity[0].value;
      let minPnlTs = equity[0].time;
      let maxPnl = equity[0].value;
      let maxPnlTs = equity[0].time;
  
      equity.forEach((pt) => {
        if (pt.value < minPnl) { minPnl = pt.value; minPnlTs = pt.time; }
        if (pt.value > maxPnl) { maxPnl = pt.value; maxPnlTs = pt.time; }
      });
  
      const start = equity[0].time;
      const end = equity[equity.length - 1].time;
      const totalPnl = equity[equity.length - 1].value;
  
      setSummary({
        totalPnl,
        maxDd: drawdownResult.maxDrawdown,
        maxDdPct: drawdownResult.maxDdPct,
        start,
        end,
        minPnl,
        minPnlTs,
        maxPnl,
        maxPnlTs,
        maxDdTs: drawdownResult.maxDrawdownTs,
      });
  
      setEquityData(equity);
      setDrawdownData(drawdown);
      lineSeriesRef.current.setData(equity);
      ddSeriesRef.current.setData(drawdown);
      chartRef.current.timeScale().fitContent();
    }
    load();
  }, [level, brokerId, selectedBasket, selectedPosition, userId]);
  
  const formatMoney = (n) => {
    const val = Number(n || 0);
    return `${val < 0 ? "-" : ""}₹${Math.abs(val).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

  const formatPct = (n) => `${Number(n || 0).toFixed(2)}%`;

  const formatTime = (unix) => {
    if (!unix) return "-";
    const d = new Date(unix * 1000);
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div>
      {showSummary && (
        <div className="pnl-summary-container">
          <div className="summary-cards">
            <strong>Total PnL</strong>
            <p className={`summary-value ${summary.totalPnl > 0 ? "text-success" : "text-danger"}`}>
              {formatMoney(summary.totalPnl)}
            </p>
          </div>
          <div className="summary-cards">
            <strong>Max PnL</strong>
            <p className={`summary-value ${summary.maxPnl > 0 ? "text-success" : "text-danger"}`}>
              {formatMoney(summary.maxPnl)}
            </p>
            <small className="summary-timestamp">{formatTime(summary.maxPnlTs)}</small>
          </div>
          <div className="summary-cards">
            <strong>Min PnL</strong>
            <p className={`summary-value ${summary.minPnl > 0 ? "text-success" : "text-danger"}`}>
              {formatMoney(summary.minPnl)}
            </p>
            <small className="summary-timestamp">{formatTime(summary.minPnlTs)}</small>
          </div>
          <div className="summary-cards">
            <strong>Max Drawdown</strong>
            <p className={`summary-value ${summary.maxDd > 0 ? "text-success" : "text-danger"}`}>
              {formatMoney(summary.maxDd)}
            </p>
            <small className="summary-timestamp">{formatTime(summary.maxDdTs)}</small>
          </div>
        </div>
      )}
      
      <div className="pnl-legend-container">
        <div className="pnl-legend-item">
          <span className="pnl-legend-indicator" />
          <strong>PNL</strong>
        </div>
        <div className="pnl-legend-item">
          <span className="pnl-legend-indicator-dd" />
          <strong>Drawdown</strong>
        </div>
      </div>
      
      <div 
        className="pnl-chart-wrapper"
        style={{ height }}
      >
        <div ref={containerRef} className="pnl-chart-container" />
        <div className="pnl-watermark">
          <img src={logo} alt="FNOTrader Logo" className="pnl-watermark-logo" />
          <span className="pnl-watermark-text">
            FNOTrader - QuikTrade
          </span>
        </div>
      </div>
    </div>
  );
};

export { PnlChart };
export default PnlChart;